import { stringify } from 'querystring'

export const state = () => ({
  snapshots: { data: [], total: 0 },
  appliedSnapshot: null,
  viewingMode: false,
  hasChanges: false,
  latestSnapshotState: '',
  selectedSnapshot: null,
  importSimplifiedEditing: false
})

export const getters = {
  selectedSnapshot: (state) => {
    return state.selectedSnapshot
  },
  snapshots (state) {
    return state.snapshots
  },
  hasChanges (state) {
    return state.hasChanges
  },
  latestSnapshotState (state) {
    return state.latestSnapshotState
  },
  lastSnapshotId (state) {
    return state?.snapshots?.data[0]?._id
  },
  viewingMode (state) {
    return state.viewingMode
  },
  userCanEdit (state, getters) {
    return getters.checkedOutByUser && !getters.viewingMode
  },
  appliedSnapshot (state) {
    return state.appliedSnapshot
  },
  checkedOutByUser (state, getters, rootState, rootGetters) {
    const userId = rootGetters['auth/user']?.id
    if (!userId) return
    const isCheckedOutByUser = (snap) =>
      snap?._source?.status === 'checkedout' &&
      snap?._source?.checkedOutBy?.id === userId

    if (isCheckedOutByUser(state.appliedSnapshot)) {
      return state.appliedSnapshot
    }
    return state.snapshots?.data?.find(isCheckedOutByUser)
  },
  checkedOut (state) {
    return state.snapshots?.data?.find(
      (snap) => snap?._source?.status === 'checkedout'
    )
  },
  importSimplifiedEditing (state) {
    return state.importSimplifiedEditing
  }
}

export const mutations = {
  selectedSnapshot (state, snapshot) {
    state.selectedSnapshot = snapshot
  },
  setHasChanges (state, value) {
    state.hasChanges = value
  },
  clear (state) {
    state.snapshots = { data: [], total: 0 }
    state.appliedSnapshot = null
  },
  snapshots (state, snapshots) {
    state.snapshots = snapshots
  },
  updateDialogNodes (state, dialogNodes) {
    if (state.appliedSnapshot) {
      // eslint-disable-next-line camelcase
      state.appliedSnapshot._source.data.dialog_nodes = dialogNodes
    } else {
      throw new Error('No applied snapshot?!')
    }
  },
  setAppliedSnapshot (state, snapshot) {
    // eslint-disable-next-line camelcase
    delete snapshot?._source?.data
    delete snapshot?._source?.changes
    state.appliedSnapshot = snapshot
  },
  view (state, snap) {
    state.appliedSnapshot = snap
    state.viewingMode = true
  },
  setViewingMode (state, mode) {
    state.viewingMode = mode
  },
  deleteSnapshot (state, snapshot) {
    state.snapshots = state.snapshots.filter(
      (snap) => snap._id === snapshot._id
    )
  },
  setImportSimplifiedEditing (state, active) {
    state.importSimplifiedEditing = active
  },
  latestSnapshotState (state, value) {
    state.latestSnapshotState = value
  }
}

export const actions = {
  async checkout ({ commit, state, rootGetters, getters }, snapshot) {
    if (snapshot?._id && snapshot._id === getters.checkedOutByUser?._id) {
      // console.log(`Snapshot already checkedout`)
      // TODO: Show message to user
      return
    }
    try {
      if (getters.checkedOutByUser) {
        // console.log(`deleing checkedout`, getters.checkedOutByUser)
        await this._vm.$axios.delete(
          `/snapshots/${getters.checkedOutByUser._id}`
        )
      }
      const result = await this._vm.$axios.post(
        `/snapshots/${snapshot._id}/checkout`
      )
      commit('setViewingMode', false)
      if (result?.data?._id) {
        commit('setAppliedSnapshot', result?.data)
      }
    } catch (error) {
      throw new Error(error.response?.data?.error || error.response?.data)
    }
  },
  async populate ({ commit, dispatch, rootGetters, rootState }, query) {
    try {
      const activeWorkspace = rootGetters['workspaces/active']
      if (!activeWorkspace) return null
      const snapshotsReq = await this._vm.$axios.get('/snapshots', {
        params: {
          noData: true,
          workspace: activeWorkspace.id,
          perPage: query.perPage,
          page: query.page,
          q: query.searchQuery || null,
          fields: ['status', 'updatedBy', 'updatedAt', 'createdBy', 'createdAt', 'checkedOutBy', 'description', 'workspace']
        },
        paramsSerializer: params => {
          return stringify(params)
        },
        headers: {
          Authorization: `Bearer ${rootGetters['auth/token']}`
        }
      })
      const snapshots = snapshotsReq.data
      snapshots.data.forEach((snap) => {
        delete snap._source.changes
      })
      commit('snapshots', snapshots)

      return snapshots
    } catch (e) {
      if (e.isAxiosError) {
        /** @type {import('axios').AxiosError} */
        const error = e
        if (error?.response?.status === 401) {
          await dispatch('auth/logout', null, { root: true })
        }
      } else {
        // console.log(`ERROR in list snapshots from api:`)
        // console.error(e)
        // console.log(Object.keys(e))
      }
      return null
    }
  },
  async publish ({ commit, getters }, snapshot) {
    // if (snapshot?._id && snapshot._id !== getters.checkedOutByUser?._id) {
    if (!snapshot._id) {
      throw new Error('Can only publish checkedout snapshot')
    }
    try {
      await this._vm.$axios.post(`/snapshots/${snapshot._id}/publish`)
      commit('setAppliedSnapshot', null)
    } catch (error) {
      throw error
      // console.log(error)
      // console.log(error.response, error.request)
    }
  },
  async publishSimplifiedEditing ({ commit, getters, rootGetters }, snapshot) {
    const activeWorkspace = rootGetters['workspaces/active']

    // if (snapshot?._id && snapshot._id !== getters.checkedOutByUser?._id) {
    if (!snapshot._id) {
      throw new Error('Can only publish checkedout snapshot')
    }
    try {
      await this._vm.$axios.post(`/chatbuilder/${activeWorkspace.id}/${snapshot._id}/publish`)
      commit('setAppliedSnapshot', null)
    } catch (error) {
      throw error
      // console.log(error)
      // console.log(error.response, error.request)
    }
  },
  async steal ({ commit, getters }, snapshot) {
    if (snapshot?._id && snapshot._id !== getters.checkedOut?._id) {
      throw new Error('Can only steal checkedout snapshot')
    }
    try {
      await this._vm.$axios.post(`/snapshots/${snapshot._id}/steal`)
      const newSnap = await this._vm.$axios.get(`/snapshots/${snapshot._id}`)
      if (newSnap?.data) {
        delete newSnap.data._source.data
        commit('setAppliedSnapshot', newSnap.data)
      } else {
        commit('setViewingMode', false)
      }
    } catch (error) {
      // console.log(error)
      // console.log(error.response, error.request)
    }
  },
  async save ({ getters }, { id, data }) {
    try {
      if (id && id !== getters.checkedOutByUser?._id) {
        throw new Error('SNAPSHOT_MUST_BE_CHECKEDOUT')
      } else if (getters.viewingMode) {
        throw new Error('IN_VIEWING_MODE')
      }
      await this._vm.$axios.patch(`/snapshots/${id}`, { data })
    } catch (error) {
      if (error.response?.data) {
        throw new Error(error.response.data.error || error.response.data)
      }
    }
  },
  hasChanges ({ commit }, value) {
    commit('hasChanges', value)
  },
  setSelectedSnapshot ({ commit }, snapshot) {
    commit('selectedSnapshot', snapshot)
  },
  latestSnapshotState ({ commit }, value) {
    commit('latestSnapshotState', value)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
